import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { ButtonModule } from '@uzurv/afp/button/module';
import { MobilityProfileDetail } from '@uzurv/clara';
import { CamelCasePipe, isNullOrUndefined } from '@uzurv/core';
import { ConfirmationDialogModule } from '../dialogs/confirmation-dialog/confirmation-dialog.module';

type MobilityProfileVariant = 'newItinerary' | 'tripDetail';

interface MappedMobilityProfileDetail {
  lastUpdated?: Date;
  mobilityAid: {
    label: string;
    options: Array<{
      label: string;
      options?: Array<{ label: string }>;
    }>;
  };
  mobilityAssistance: {
    label: string;
    options?: Array<string>;
  };
  medicalAndHealth: {
    label: string;
    options?: Array<string>;
  };
}

@Component({
  selector: 'app-rider-mobility-profile-detail',
  templateUrl: './rider-mobility-profile-detail.component.html',
  styleUrls: ['./rider-mobility-profile-detail.component.scss'],
  imports: [CommonModule, ButtonModule, ConfirmationDialogModule, CamelCasePipe],
  standalone: true
})
export class RiderMobilityProfileDetailComponent implements OnChanges {
  @Input() riderMobilityProfile: MobilityProfileDetail;
  @Input() variant: MobilityProfileVariant = 'tripDetail';

  updatedMobilityProfileDetail: MappedMobilityProfileDetail;

  ngOnChanges(): void {
    this.updatedMobilityProfileDetail = this.mapMobilityProfileDetail(this.riderMobilityProfile);
  }

  mapMobilityProfileDetail(mobilityProfileDetail: MobilityProfileDetail): MappedMobilityProfileDetail {
    if (!mobilityProfileDetail || mobilityProfileDetail.creationNeeded) {
      return null;
    }

    const mobilityAidLabels = {
      manualWheelchair: 'Uses a manual wheelchair',
      powerWheelchair: 'Uses a power wheelchair',
      electricScooter: 'Uses an electric scooter',
      bariatricWheelchair: 'Uses a bariatric wheelchair',
      hasWalker: 'Uses a walker',
      hasCane: 'Uses a cane',
      hasRollator: 'Uses a rollator'
    };
    const mobilityAssistanceLabels = {
      isStairAssistanceRequired: 'Needs stair assistance',
      hasServiceAnimal: 'Has a service animal',
      hasPersonalCareAssistant: 'Has a personal care assistant'
    };
    const medicalAndHealthLabels = {
      hasHearingImpairment: 'Has a hearing impairment',
      hasVisionImpairment: 'Has a vision impairment',
      hasIntellectualDisability: 'Has an intellectual or developmental disability',
      hasPortableOxygen: 'Travels with portable oxygen'
    };

    const mobilityAidOptions = [
      'manualWheelchair',
      'powerWheelchair',
      'electricScooter',
      'bariatricWheelchair',
      'hasWalker',
      'hasCane',
      'hasRollator'
    ]
      .map(item => {
        if (isNullOrUndefined(mobilityProfileDetail[item]) || !mobilityProfileDetail[item]) {
          return null;
        }

        if (mobilityProfileDetail[item] === true) {
          return {
            label: mobilityAidLabels[item]
          };
        } else {
          const mobilityAidData = mobilityProfileDetail[item];
          const options = [];

          if (['manualWheelchair', 'electricScooter'].includes(item)) {
            if (!isNullOrUndefined(mobilityAidData?.seatTransferAssistanceRequired)) {
              if (mobilityAidData?.seatTransferAssistanceRequired) {
                options.push({ label: 'Cannot transfer into a car without assistance' });
              } else {
                options.push({ label: 'Can transfer into a car without assistance' });
              }
            }
          }

          const vehicleEquipmentType = mobilityAidData.vehicleEquipmentType;
          if (!isNullOrUndefined(!mobilityAidData?.seatTransferAssistanceRequired)) {
            if (mobilityAidData?.seatTransferAssistanceRequired && vehicleEquipmentType === null) {
              options.push({ label: 'No preference on vehicle loading type' });
            } else if (vehicleEquipmentType === 'LIFT') {
              options.push({ label: 'Needs lift loading vehicle' });
            } else if (vehicleEquipmentType === 'RAMP') {
              options.push({ label: 'Needs ramp loading vehicle' });
            }
          }

          return {
            label: mobilityAidLabels[item],
            options
          };
        }
      })
      .filter(item => item !== null);

    const mobilityAssistanceOptions = ['isStairAssistanceRequired', 'hasServiceAnimal', 'hasPersonalCareAssistant']
      .map(item => {
        const options = [];
        if (mobilityProfileDetail[item] === true) {
          const mobilityAssistanceData = mobilityProfileDetail[item];
          if (mobilityAssistanceData) {
            options.push({ label: mobilityAssistanceData });
          }
        } else {
          return null;
        }
        return {
          label: mobilityAssistanceLabels[item],
          options
        };
      })
      .filter(item => item !== null);

    const medicalAndHealthOptions = ['hasHearingImpairment', 'hasVisionImpairment', 'hasIntellectualDisability', 'hasPortableOxygen']
      .map(item => {
        const options = [];
        if (mobilityProfileDetail[item] === true) {
          const medicalAndHealthData = mobilityProfileDetail[item];
          if (medicalAndHealthData) {
            options.push({ label: medicalAndHealthLabels[item] });
          }
        } else {
          return null;
        }
        return {
          label: medicalAndHealthLabels[item],
          options
        };
      })
      .filter(item => item !== null);

    const showNone = this.variant !== 'tripDetail';
    const mappedMobilityProfileDetail: MappedMobilityProfileDetail = {
      lastUpdated: mobilityProfileDetail.modifiedDate,
      mobilityAid: {
        label: 'Mobility Aids',
        options: mobilityAidOptions.length > 0 ? mobilityAidOptions : showNone ? [{ label: 'None' }] : []
      },
      mobilityAssistance: {
        label: 'Mobility Assistance',
        options: mobilityAssistanceOptions.length > 0 ? mobilityAssistanceOptions.map(option => option.label) : showNone ? ['None'] : []
      },
      medicalAndHealth: {
        label: 'Disabilities and Health',
        options: medicalAndHealthOptions.length > 0 ? medicalAndHealthOptions.map(option => option.label) : showNone ? ['None'] : []
      }
    };
    return mappedMobilityProfileDetail;
  }
}
